import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import { Icon } from '@iconify/react';
import {
  Button, Badge, Modal, SectionHeader, Divider,
} from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';

// ─── Types ──────────────────────────────────────────────────

interface Payment {
  id: string;
  amount: number;
  amountPaid: number;
  dueDate: string;
  paidDate?: string;
  status: string;
  receiptNumber?: string;
}

interface Sale {
  id: string;
  lotNumber: string;
  sectionId: string;
  clientName: string;
  clientId: string;
  agentName: string;
  saleAmount: number;
  paymentType: string;
  stage: string;
  commission: number;
  downPayment: number;
  totalPaid: number;
  balance: number;
  installmentMonths?: number;
  notes?: string;
  contractDate?: string;
  completedDate?: string;
  createdAt: string;
  payments: Payment[];
}

type ViewMode = 'kanban' | 'list';

const STAGES = ['INQUIRY', 'RESERVATION', 'CONTRACT_SIGNING', 'PAYMENT', 'COMPLETED'] as const;

const STAGE_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  INQUIRY: { label: 'Inquiry', color: '#94A3B8', bg: 'rgba(148,163,184,0.12)', icon: 'solar:chat-round-bold-duotone' },
  RESERVATION: { label: 'Reservation', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', icon: 'solar:bookmark-bold-duotone' },
  CONTRACT_SIGNING: { label: 'Contract', color: '#3B82F6', bg: 'rgba(59,130,246,0.12)', icon: 'solar:document-text-bold-duotone' },
  PAYMENT: { label: 'Payment', color: '#0D9488', bg: 'rgba(13,148,136,0.12)', icon: 'solar:wallet-money-bold-duotone' },
  COMPLETED: { label: 'Completed', color: '#22C55E', bg: 'rgba(34,197,94,0.12)', icon: 'solar:check-circle-bold-duotone' },
  CANCELLED: { label: 'Cancelled', color: '#EF4444', bg: 'rgba(239,68,68,0.12)', icon: 'solar:close-circle-bold-duotone' },
};

// ─── Map backend response to frontend Sale shape ────────────
const mapApiSale = (s: any): Sale => ({
  id: s.id,
  lotNumber: s.lot?.lotNumber || '—',
  sectionId: s.lot?.sectionId || '',
  clientName: s.client ? `${s.client.firstName} ${s.client.lastName}` : '—',
  clientId: s.clientId || s.client?.id || '',
  agentName: s.agent?.name || '—',
  saleAmount: s.saleAmount,
  paymentType: s.paymentType,
  stage: s.stage,
  commission: s.commission,
  downPayment: s.downPayment,
  totalPaid: s.totalPaid,
  balance: s.balance,
  installmentMonths: s.installmentMonths,
  notes: s.notes,
  contractDate: s.contractDate,
  completedDate: s.completedDate,
  createdAt: s.createdAt,
  payments: (s.payments || []).map((p: any) => ({
    id: p.id, amount: p.amount, amountPaid: p.amountPaid,
    dueDate: p.dueDate, paidDate: p.paidDate, status: p.status, receiptNumber: p.receiptNumber,
  })),
});

const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const formatCurrency = (n: number) => `₱${n.toLocaleString()}`;

// ─── Component ──────────────────────────────────────────────

const Sales: React.FC = () => {
  const { addToast } = useToast();
  const location = useLocation();
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.sales.list();
      setSales((data.sales || []).map(mapApiSale));
    } catch (err: any) {
      addToast('error', 'Fetch Error', err.message || 'Failed to load sales');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { fetchSales(); }, [fetchSales]);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const hasShownToast = useRef(false);

  // Auto-select sale if navigated from Payments with saleId
  useEffect(() => {
    const state = location.state as { saleId?: string } | null;
    if (state?.saleId && sales.length > 0 && !hasShownToast.current) {
      const sale = sales.find(s => s.id === state.saleId);
      if (sale) {
        setSelectedSale(sale);
        addToast('info', 'Sale Details', `Viewing sale for ${sale.clientName}`);
        hasShownToast.current = true;
      }
    }
  }, [location.state, sales, addToast]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payingPayment, setPayingPayment] = useState<Payment | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelingSale, setCancelingSale] = useState<Sale | null>(null);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [refundCalculation, setRefundCalculation] = useState<any>(null);
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [refundMethod, setRefundMethod] = useState('CASH');
  const [refundReference, setRefundReference] = useState('');
  const [refundNotes, setRefundNotes] = useState('');

  // Drag-and-drop state
  const [draggedSale, setDraggedSale] = useState<Sale | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [showStageConfirm, setShowStageConfirm] = useState(false);
  const [pendingStageChange, setPendingStageChange] = useState<{ sale: Sale; newStage: string } | null>(null);

  // ─── Stage transition ─────────────────────────────────
  const moveStage = async (saleId: string, newStage: string) => {
    try {
      await api.sales.updateStage(saleId, newStage);
      setSales(prev => prev.map(s => {
        if (s.id !== saleId) return s;
        const updated = { ...s, stage: newStage };
        if (newStage === 'CONTRACT_SIGNING') updated.contractDate = new Date().toISOString();
        if (newStage === 'COMPLETED') { updated.completedDate = new Date().toISOString(); updated.balance = 0; updated.totalPaid = s.saleAmount; }
        return updated;
      }));
      if (selectedSale?.id === saleId) {
        setSelectedSale(prev => prev ? { ...prev, stage: newStage } : null);
      }
      addToast('success', 'Stage Updated', `Sale moved to ${STAGE_CONFIG[newStage]?.label || newStage}`);
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to update stage');
    }
  };

  // ─── Drag handlers ────────────────────────────────────
  const handleDragStart = (e: React.DragEvent, sale: Sale) => {
    setDraggedSale(sale);
    e.dataTransfer.effectAllowed = 'move';
    (e.currentTarget as HTMLElement).style.opacity = '0.4';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = '1';
    setDraggedSale(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e: React.DragEvent, stage: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stage);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = (e: React.DragEvent, newStage: string) => {
    e.preventDefault();
    setDragOverStage(null);
    
    if (!draggedSale || draggedSale.stage === newStage) {
      setDraggedSale(null);
      return;
    }

    // Show confirmation modal with full sale object
    setPendingStageChange({ sale: draggedSale, newStage });
    setShowStageConfirm(true);
  };

  const confirmStageChange = () => {
    if (pendingStageChange) {
      moveStage(pendingStageChange.sale.id, pendingStageChange.newStage);
    }
    setShowStageConfirm(false);
    setPendingStageChange(null);
    setDraggedSale(null);
  };

  const cancelStageChange = () => {
    setShowStageConfirm(false);
    setPendingStageChange(null);
    setDraggedSale(null);
  };

  // ─── Record payment ───────────────────────────────────
  const recordPayment = async () => {
    if (!payingPayment || !selectedSale) return;
    setRecordingPayment(true);
    try {
      await api.sales.recordPayment(payingPayment.id, { amountPaid: payingPayment.amount });
      const updatedPayments = selectedSale.payments.map(p =>
        p.id === payingPayment.id
          ? { ...p, amountPaid: p.amount, paidDate: new Date().toISOString(), status: 'PAID' }
          : p
      );
      const totalFromPayments = updatedPayments.reduce((sum, p) => sum + p.amountPaid, 0);
      const newTotalPaid = selectedSale.downPayment + totalFromPayments;
      const updatedSale = { ...selectedSale, payments: updatedPayments, totalPaid: newTotalPaid, balance: selectedSale.saleAmount - newTotalPaid };

      setSelectedSale(updatedSale);
      setSales(prev => prev.map(s => s.id === updatedSale.id ? updatedSale : s));
      setShowPaymentModal(false);
      setPayingPayment(null);
      addToast('success', 'Payment Recorded', `${formatCurrency(payingPayment.amount)} payment recorded successfully.`);
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleCancelSale = async (sale: Sale) => {
    setCancelingSale(sale);
    setShowCancelConfirm(true);
    setRefundCalculation(null);
    setRefundMethod('CASH');
    setRefundReference('');
    setRefundNotes('');
    
    // Calculate refund amount
    setLoadingRefund(true);
    try {
      const calc = await api.refunds.calculate({ saleId: sale.id });
      setRefundCalculation(calc);
    } catch (err: any) {
      console.error('Refund calculation error:', err);
      // If no policy or calculation fails, continue without refund
      setRefundCalculation({ error: err.message || 'Unable to calculate refund' });
    } finally {
      setLoadingRefund(false);
    }
  };

  const confirmCancelSale = async () => {
    if (!cancelingSale) return;
    setProcessingRefund(true);
    try {
      // Process refund if applicable
      if (refundCalculation && !refundCalculation.error && refundCalculation.calculation?.netRefundAmount > 0) {
        await api.refunds.process({
          saleId: cancelingSale.id,
          refundAmount: refundCalculation.calculation.netRefundAmount,
          refundMethod,
          referenceNumber: refundReference,
          notes: refundNotes,
        });
        addToast('success', 'Sale Cancelled & Refund Processed', `Refund of ${formatCurrency(refundCalculation.calculation.netRefundAmount)} processed successfully.`);
      } else {
        // Just cancel without refund
        await moveStage(cancelingSale.id, 'CANCELLED');
        addToast('success', 'Sale Cancelled', 'Sale has been cancelled.');
      }
      
      setShowCancelConfirm(false);
      setCancelingSale(null);
      setSelectedSale(null);
      fetchSales(); // Refresh sales list
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to cancel sale');
    } finally {
      setProcessingRefund(false);
    }
  };

  // ─── Kanban View ──────────────────────────────────────

  const renderKanban = () => (
    <div style={{
      display: 'grid', gridTemplateColumns: `repeat(${STAGES.length}, 1fr)`, gap: '16px',
      overflowX: 'auto', minHeight: '500px',
    }}>
      {STAGES.map((stage) => {
        const config = STAGE_CONFIG[stage];
        const stageSales = sales.filter(s => s.stage === stage);
        return (
          <div 
            key={stage} 
            style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px',
              background: dragOverStage === stage ? 'rgba(13,148,136,0.05)' : 'transparent',
              borderRadius: 'var(--radius-md)',
              padding: dragOverStage === stage ? '8px' : '0',
              transition: 'all 0.2s ease',
            }}
            onDragOver={(e) => handleDragOver(e, stage)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, stage)}
          >
            {/* Column Header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px',
              background: config.bg, borderRadius: 'var(--radius-md)',
            }}>
              <Icon icon={config.icon} width={18} style={{ color: config.color }} />
              <span style={{ color: config.color, fontWeight: 600, fontSize: '13px', flex: 1 }}>{config.label}</span>
              <span style={{
                background: 'var(--bg-elevated)', borderRadius: 'var(--radius-full)',
                padding: '2px 8px', fontSize: '11px', fontWeight: 700, color: config.color,
              }}>{stageSales.length}</span>
            </div>

            {/* Cards */}
            {stageSales.map((sale) => {
              const paymentProgress = sale.paymentType === 'INSTALLMENT' ? (sale.totalPaid / sale.saleAmount) * 100 : 100;
              return (
                <div
                  key={sale.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, sale)}
                  onDragEnd={handleDragEnd}
                  onClick={() => setSelectedSale(sale)}
                  style={{
                    background: dragOverStage === stage && draggedSale?.id !== sale.id 
                      ? 'rgba(13,148,136,0.08)' 
                      : 'var(--glass-bg)',
                    backdropFilter: 'var(--glass-blur)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '16px',
                    cursor: 'grab',
                    transition: 'all 0.2s ease',
                    borderTop: `3px solid ${config.color}`,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                  }}
                >
                  {/* Header: Lot Number & Section */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '8px',
                      background: `${config.color}15`, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Icon icon="solar:map-point-bold-duotone" width={16} style={{ color: config.color }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '14px', lineHeight: 1.2 }}>
                        Lot {sale.lotNumber}
                      </div>
                      <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginTop: '2px' }}>
                        Section {sale.sectionId}
                      </div>
                    </div>
                    <Badge variant={sale.paymentType === 'CASH' ? 'available' : 'info' as any} 
                      style={{ fontSize: '9px', padding: '2px 6px', fontWeight: 700 }}>
                      {sale.paymentType}
                    </Badge>
                  </div>

                  {/* Client Name */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px', 
                    marginBottom: '12px',
                    paddingBottom: '12px',
                    borderBottom: '1px solid var(--border-base)',
                  }}>
                    <Icon icon="solar:user-bold-duotone" width={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <span style={{ 
                      color: 'var(--text-secondary)', 
                      fontSize: '13px', 
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {sale.clientName}
                    </span>
                  </div>

                  {/* Sale Amount */}
                  <div style={{ marginBottom: sale.paymentType === 'INSTALLMENT' ? '10px' : '12px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'baseline', 
                      gap: '4px',
                      marginBottom: '2px',
                    }}>
                      <Icon icon="solar:wallet-money-bold-duotone" width={12} style={{ color: 'var(--text-muted)' }} />
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Sale Amount
                      </span>
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '16px', fontFamily: 'var(--font-heading)' }}>
                      {formatCurrency(sale.saleAmount)}
                    </div>
                  </div>

                  {/* Payment Progress (Installment only) */}
                  {sale.paymentType === 'INSTALLMENT' && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>
                          Payment Progress
                        </span>
                        <span style={{ fontSize: '11px', color: config.color, fontWeight: 700 }}>
                          {Math.round(paymentProgress)}%
                        </span>
                      </div>
                      <div style={{ 
                        height: '6px', 
                        borderRadius: 'var(--radius-full)', 
                        background: 'var(--bg-elevated)',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          height: '100%', 
                          borderRadius: 'var(--radius-full)', 
                          background: `linear-gradient(90deg, ${config.color}, ${config.color}dd)`,
                          width: `${Math.min(100, paymentProgress)}%`,
                          transition: 'width 0.4s ease',
                          boxShadow: `0 0 8px ${config.color}40`,
                        }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                        <span style={{ fontSize: '10px', color: 'var(--text-faint)' }}>
                          Paid: {formatCurrency(sale.totalPaid)}
                        </span>
                        <span style={{ fontSize: '10px', color: sale.balance > 0 ? 'var(--warning)' : 'var(--success)', fontWeight: 600 }}>
                          Bal: {formatCurrency(sale.balance)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Agent */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    paddingTop: '10px',
                    borderTop: '1px solid var(--border-base)',
                  }}>
                    <Icon icon="solar:user-id-bold-duotone" width={12} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
                    <span style={{ fontSize: '11px', color: 'var(--text-faint)', fontWeight: 500 }}>
                      {sale.agentName}
                    </span>
                  </div>
                </div>
              );
            })}

            {stageSales.length === 0 && (
              <div style={{
                padding: '24px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '12px',
                border: '1px dashed rgba(148,163,184,0.15)', borderRadius: 'var(--radius-md)',
              }}>
                No deals
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // ─── List View ────────────────────────────────────────

  const renderList = () => (
    <div style={{
      background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)',
      border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden',
    }}>
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>
          <tr>
            {['Lot', 'Client', 'Agent', 'Amount', 'Type', 'Stage', 'Balance', 'Created'].map(h => (
              <th key={h} style={{
                background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontSize: '12px',
                fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' as const,
                padding: '12px 16px', textAlign: 'left' as const,
                borderBottom: '1px solid rgba(148,163,184,0.10)',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sales.map(sale => (
            <tr key={sale.id} onClick={() => setSelectedSale(sale)} style={{ cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(13,148,136,0.06)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              <td style={{ padding: '14px 16px', color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px', borderBottom: '1px solid var(--border-base)' }}>
                {sale.lotNumber} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({sale.sectionId})</span>
              </td>
              <td style={{ padding: '14px 16px', color: 'var(--text-secondary)', fontSize: '14px', borderBottom: '1px solid var(--border-base)' }}>{sale.clientName}</td>
              <td style={{ padding: '14px 16px', color: 'var(--text-muted)', fontSize: '13px', borderBottom: '1px solid var(--border-base)' }}>{sale.agentName}</td>
              <td style={{ padding: '14px 16px', color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px', borderBottom: '1px solid var(--border-base)' }}>{formatCurrency(sale.saleAmount)}</td>
              <td style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-base)' }}>
                <Badge variant={sale.paymentType === 'CASH' ? 'available' : 'info' as any}>{sale.paymentType}</Badge>
              </td>
              <td style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-base)' }}>
                <Badge variant={
                  sale.stage === 'COMPLETED' ? 'available' :
                  sale.stage === 'CANCELLED' ? 'sold' : 'reserved' as any
                }>{STAGE_CONFIG[sale.stage]?.label || sale.stage}</Badge>
              </td>
              <td style={{ padding: '14px 16px', color: sale.balance > 0 ? 'var(--warning)' : 'var(--success)', fontWeight: 600, fontSize: '14px', borderBottom: '1px solid var(--border-base)' }}>
                {formatCurrency(sale.balance)}
              </td>
              <td style={{ padding: '14px 16px', color: 'var(--text-muted)', fontSize: '13px', borderBottom: '1px solid var(--border-base)' }}>{formatDate(sale.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ─── Sale Detail Modal ────────────────────────────────

  const renderDetailModal = () => {
    if (!selectedSale) return null;
    const s = selectedSale;
    const config = STAGE_CONFIG[s.stage];
    const currentIdx = STAGES.indexOf(s.stage as any);
    const canAdvance = currentIdx >= 0 && currentIdx < STAGES.length - 1 && s.stage !== 'CANCELLED';
    const nextStage = canAdvance ? STAGES[currentIdx + 1] : null;

    return (
      <Modal isOpen={!!selectedSale} onClose={() => setSelectedSale(null)} title={`Sale — Lot ${s.lotNumber}`} width="700px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSelectedSale(null)}>Close</Button>
            {s.stage !== 'COMPLETED' && s.stage !== 'CANCELLED' && (
              <Button variant="danger" size="sm"
                onClick={() => handleCancelSale(s)}>
                Cancel Sale
              </Button>
            )}
            {nextStage && (
              <Button variant="primary" icon={STAGE_CONFIG[nextStage].icon}
                onClick={() => moveStage(s.id, nextStage)}>
                Move to {STAGE_CONFIG[nextStage].label}
              </Button>
            )}
          </>
        }
      >
        {/* Stage Progress */}
        <div style={{ 
          background: 'var(--bg-elevated)', 
          borderRadius: 'var(--radius-lg)', 
          padding: '16px',
          marginBottom: '20px',
        }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '12px' }}>
            Pipeline Progress
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {STAGES.map((st, i) => {
              const isActive = i <= currentIdx;
              const stConfig = STAGE_CONFIG[st];
              const isCurrent = i === currentIdx;
              return (
                <div key={st} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    height: '6px', width: '100%', borderRadius: 'var(--radius-full)',
                    background: isActive ? `linear-gradient(90deg, ${stConfig.color}, ${stConfig.color}dd)` : 'var(--border-base)',
                    transition: 'all 0.3s ease',
                    boxShadow: isCurrent ? `0 0 8px ${stConfig.color}40` : 'none',
                  }} />
                  <span style={{ 
                    fontSize: '10px', 
                    color: isActive ? stConfig.color : 'var(--text-faint)', 
                    fontWeight: isCurrent ? 700 : isActive ? 600 : 400,
                    textAlign: 'center',
                  }}>
                    {stConfig.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Client & Agent Info */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '12px', 
          marginBottom: '20px',
        }}>
          <div style={{ 
            background: 'var(--bg-elevated)', 
            borderRadius: 'var(--radius-lg)', 
            padding: '14px',
            borderLeft: '3px solid var(--primary-600)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <Icon icon="solar:user-bold-duotone" width={14} style={{ color: 'var(--text-muted)' }} />
              <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Client
              </span>
            </div>
            <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 600 }}>
              {s.clientName}
            </div>
          </div>
          <div style={{ 
            background: 'var(--bg-elevated)', 
            borderRadius: 'var(--radius-lg)', 
            padding: '14px',
            borderLeft: '3px solid var(--info)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <Icon icon="solar:user-id-bold-duotone" width={14} style={{ color: 'var(--text-muted)' }} />
              <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Agent
              </span>
            </div>
            <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 600 }}>
              {s.agentName}
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div style={{ 
          background: 'var(--bg-elevated)', 
          borderRadius: 'var(--radius-lg)', 
          padding: '16px',
          marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
            <Icon icon="solar:wallet-money-bold-duotone" width={16} style={{ color: config.color }} />
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Financial Details
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            {[
              { icon: 'solar:tag-price-bold-duotone', label: 'Sale Amount', value: formatCurrency(s.saleAmount), color: 'var(--text-primary)' },
              { icon: 'solar:card-bold-duotone', label: 'Down Payment', value: formatCurrency(s.downPayment), color: 'var(--text-secondary)' },
              { icon: 'solar:check-circle-bold-duotone', label: 'Total Paid', value: formatCurrency(s.totalPaid), color: 'var(--success)' },
              { icon: 'solar:clock-circle-bold-duotone', label: 'Balance', value: formatCurrency(s.balance), color: s.balance > 0 ? 'var(--warning)' : 'var(--success)' },
              { icon: 'solar:hand-money-bold-duotone', label: 'Commission', value: formatCurrency(s.commission), color: 'var(--text-secondary)' },
              { icon: 'solar:calendar-bold-duotone', label: 'Installments', value: s.installmentMonths ? `${s.installmentMonths} months` : '—', color: 'var(--text-secondary)' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon icon={item.icon} width={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-faint)', marginBottom: '2px' }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: item.color }}>
                    {item.value}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Info */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '10px',
          marginBottom: '20px',
        }}>
          {[
            { icon: 'solar:map-point-bold-duotone', label: 'Section', value: s.sectionId },
            { icon: 'solar:card-send-bold-duotone', label: 'Payment Type', value: s.paymentType },
            { icon: 'solar:document-bold-duotone', label: 'Contract Date', value: formatDate(s.contractDate) },
          ].map((item, i) => (
            <div key={i} style={{ 
              background: 'var(--bg-elevated)', 
              borderRadius: 'var(--radius-md)', 
              padding: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Icon icon={item.icon} width={12} style={{ color: 'var(--text-muted)' }} />
                <span style={{ fontSize: '9px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {item.label}
                </span>
              </div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>

        {/* Payment Progress */}
        {s.paymentType === 'INSTALLMENT' && (
          <>
            <Divider spacing="16px" />
            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>Payment Schedule</span>
              <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                {s.payments.filter(p => p.status === 'PAID').length} / {s.payments.length} paid
              </span>
            </div>
            <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {s.payments.map((p, i) => (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 10px', borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-elevated)',
                }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '12px', width: '24px' }}>#{i + 1}</span>
                  <span style={{ flex: 1, color: 'var(--text-secondary)', fontSize: '13px' }}>{formatDate(p.dueDate)}</span>
                  <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>{formatCurrency(p.amount)}</span>
                  <Badge variant={
                    p.status === 'PAID' ? 'available' : p.status === 'OVERDUE' ? 'sold' : p.status === 'PARTIAL' ? 'reserved' : 'default' as any
                  }>{p.status}</Badge>
                  {(p.status === 'PENDING' || p.status === 'OVERDUE' || p.status === 'PARTIAL') && (
                    <Button variant="primary" size="sm" onClick={(e) => { e.stopPropagation(); setPayingPayment(p); setShowPaymentModal(true); }}
                      style={{ padding: '4px 10px', fontSize: '11px' }}>
                      Pay
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {s.notes && (
          <>
            <Divider spacing="16px" />
            <div style={{ color: 'var(--dark-500)', fontSize: '12px', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notes</div>
            <div style={{ color: 'var(--dark-300)', fontSize: '13px', lineHeight: 1.5 }}>{s.notes}</div>
          </>
        )}
      </Modal>
    );
  };

  // ─── Main Render ──────────────────────────────────────

  const totalRevenue = sales.filter(s => s.stage !== 'CANCELLED').reduce((sum, s) => sum + s.saleAmount, 0);
  const totalCollected = sales.reduce((sum, s) => sum + s.totalPaid, 0);
  const overdueCount = sales.reduce((sum, s) => sum + s.payments.filter(p => p.status === 'OVERDUE').length, 0);

  return (
    <>
      <TopBar title="Sales Pipeline" />
      <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          {[
            { label: 'Total Revenue', value: formatCurrency(totalRevenue), color: 'var(--primary-400)', bg: 'rgba(13,148,136,0.12)' },
            { label: 'Collected', value: formatCurrency(totalCollected), color: 'var(--success)', bg: 'rgba(34,197,94,0.12)' },
            { label: 'Active Deals', value: String(sales.filter(s => !['COMPLETED', 'CANCELLED'].includes(s.stage)).length), color: 'var(--info)', bg: 'rgba(59,130,246,0.12)' },
            { label: 'Overdue Payments', value: String(overdueCount), color: overdueCount > 0 ? 'var(--danger)' : 'var(--success)', bg: overdueCount > 0 ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)' },
          ].map((s, i) => (
            <div key={i} style={{
              background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)',
              border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-lg)', padding: '16px 20px',
            }}>
              <div style={{ fontSize: '12px', color: 'var(--dark-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</div>
              <div style={{ fontSize: '22px', fontWeight: 700, color: s.color, fontFamily: 'var(--font-heading)', marginTop: '4px' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SectionHeader title="Pipeline" icon="solar:sale-bold-duotone" />
          <div style={{ flex: 1 }} />
          <div style={{
            display: 'flex', borderRadius: 'var(--radius-md)', overflow: 'hidden',
            border: '1px solid var(--glass-border)',
          }}>
            {(['kanban', 'list'] as const).map(mode => (
              <button key={mode} onClick={() => setViewMode(mode)} style={{
                padding: '8px 16px', fontSize: '13px', fontWeight: 500, border: 'none', cursor: 'pointer',
                background: viewMode === mode ? 'rgba(13,148,136,0.15)' : 'rgba(15,23,42,0.30)',
                color: viewMode === mode ? 'var(--primary-400)' : 'var(--dark-400)',
                fontFamily: 'var(--font-body)', transition: 'all 0.15s ease',
              }}>
                <Icon icon={mode === 'kanban' ? 'solar:widget-bold-duotone' : 'solar:list-bold-duotone'} width={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                {mode === 'kanban' ? 'Kanban' : 'List'}
              </button>
            ))}
          </div>
        </div>

        {/* View */}
        {viewMode === 'kanban' ? renderKanban() : renderList()}
      </div>

      {/* Detail Modal */}
      {renderDetailModal()}

      {/* Stage Change Confirmation Modal */}
      <Modal
        isOpen={showStageConfirm}
        onClose={cancelStageChange}
        title="Confirm Stage Change"
        footer={
          <>
            <Button variant="secondary" onClick={cancelStageChange}>Cancel</Button>
            <Button variant="primary" icon="solar:check-circle-bold-duotone" onClick={confirmStageChange}>
              Confirm Move
            </Button>
          </>
        }
      >
        {(() => {
          if (!pendingStageChange) {
            return <div style={{ color: 'var(--dark-400)', fontSize: '14px' }}>Loading...</div>;
          }
          
          const sale = pendingStageChange.sale;
          const fromStageIndex = STAGES.indexOf(sale.stage as any);
          const toStageIndex = STAGES.indexOf(pendingStageChange.newStage as any);
          const isForward = toStageIndex > fromStageIndex;
          const fromConfig = STAGE_CONFIG[sale.stage];
          const toConfig = STAGE_CONFIG[pendingStageChange.newStage];
          
          return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Deal Info */}
              <div style={{ 
                padding: '12px', 
                background: 'rgba(148,163,184,0.05)', 
                borderRadius: 'var(--radius-md)',
                borderLeft: '3px solid var(--primary-600)',
              }}>
                <div style={{ color: 'var(--dark-100)', fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>
                  Lot {sale.lotNumber}
                </div>
                <div style={{ color: 'var(--dark-400)', fontSize: '13px' }}>
                  {sale.clientName} · {formatCurrency(sale.saleAmount)}
                </div>
              </div>

              {/* Stage Transition */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 12px' }}>
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '6px' 
                }}>
                  <div style={{ fontSize: '10px', color: 'var(--dark-600)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    From
                  </div>
                  <div style={{ 
                    background: fromConfig.bg, 
                    color: fromConfig.color, 
                    border: `1px solid ${fromConfig.color}40`,
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    borderRadius: 'var(--radius-md)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    <Icon icon={fromConfig.icon} width={14} />
                    {fromConfig.label}
                  </div>
                </div>

                <Icon 
                  icon={isForward ? 'solar:arrow-right-bold-duotone' : 'solar:arrow-left-bold-duotone'} 
                  width={24} 
                  style={{ 
                    color: isForward ? 'var(--primary-500)' : '#F59E0B',
                    flexShrink: 0,
                  }} 
                />

                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '6px' 
                }}>
                  <div style={{ fontSize: '10px', color: 'var(--dark-600)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    To
                  </div>
                  <div style={{ 
                    background: toConfig.bg, 
                    color: toConfig.color, 
                    border: `1px solid ${toConfig.color}40`,
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    borderRadius: 'var(--radius-md)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    <Icon icon={toConfig.icon} width={14} />
                    {toConfig.label}
                  </div>
                </div>
              </div>

              {/* Warning for outstanding balance */}
              {pendingStageChange.newStage === 'COMPLETED' && sale.balance > 0 && (
                <div style={{
                  padding: '10px 12px', borderRadius: '6px',
                  background: 'rgba(245,158,11,0.10)', borderLeft: '3px solid #F59E0B',
                }}>
                  <div style={{ fontSize: '12px', color: '#F59E0B', fontWeight: 600 }}>
                    ⚠️ Outstanding Balance: {formatCurrency(sale.balance)}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--dark-500)', marginTop: '2px' }}>
                    Marking as completed will clear the balance.
                  </div>
                </div>
              )}
              </div>
          );
        })()}
      </Modal>

      {/* Payment Confirmation Modal */}
      <Modal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="Record Payment" width="400px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowPaymentModal(false)} disabled={recordingPayment}>Cancel</Button>
            <Button variant="primary" icon="solar:wallet-money-bold-duotone" onClick={recordPayment} disabled={recordingPayment}>
              {recordingPayment ? 'Recording...' : 'Confirm Payment'}
            </Button>
          </>
        }>
        {payingPayment && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--dark-500)' }}>Amount</span>
              <span style={{ color: 'var(--dark-100)', fontWeight: 700, fontSize: '18px' }}>{formatCurrency(payingPayment.amount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--dark-500)' }}>Due Date</span>
              <span style={{ color: 'var(--dark-200)' }}>{formatDate(payingPayment.dueDate)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--dark-500)' }}>Status</span>
              <Badge variant={payingPayment.status === 'OVERDUE' ? 'sold' : 'default' as any}>{payingPayment.status}</Badge>
            </div>
          </div>
        )}
      </Modal>

      {/* Cancel Sale Confirmation Modal */}
      {showCancelConfirm && cancelingSale && (
        <Modal isOpen={showCancelConfirm} title="Cancel Sale & Process Refund" onClose={() => { setShowCancelConfirm(false); setCancelingSale(null); }} width="600px">
          <div style={{ padding: '8px 0' }}>
            {/* Sale Info */}
            <div style={{ 
              background: 'var(--bg-elevated)', 
              borderRadius: 'var(--radius-md)', 
              padding: '16px',
              border: '1px solid var(--border-base)',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Icon icon="solar:danger-triangle-bold-duotone" width={32} style={{ color: '#EF4444' }} />
                <div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '15px' }}>
                    Cancel This Sale?
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                    This will cancel the sale and release the lot
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Client</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>
                    {cancelingSale.clientName}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Lot</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>
                    {cancelingSale.lotNumber}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sale Amount</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '16px' }}>
                    {formatCurrency(cancelingSale.saleAmount)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Paid</div>
                  <div style={{ color: '#10B981', fontWeight: 700, fontSize: '16px' }}>
                    {formatCurrency(cancelingSale.totalPaid)}
                  </div>
                </div>
              </div>
            </div>

            {/* Refund Calculation */}
            {loadingRefund && (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
                <Icon icon="solar:refresh-bold-duotone" width={24} style={{ animation: 'spin 1s linear infinite' }} />
                <div style={{ marginTop: '8px' }}>Calculating refund...</div>
              </div>
            )}

            {!loadingRefund && refundCalculation && !refundCalculation.error && refundCalculation.calculation?.netRefundAmount > 0 && (
              <div style={{ 
                background: 'rgba(16,185,129,0.10)', 
                border: '1px solid rgba(16,185,129,0.30)',
                borderRadius: 'var(--radius-md)', 
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <Icon icon="solar:wallet-money-bold-duotone" width={28} style={{ color: '#10B981' }} />
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '16px' }}>
                      Refund Amount: {formatCurrency(refundCalculation.calculation.netRefundAmount)}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                      Policy: {refundCalculation.policy.name}
                    </div>
                  </div>
                </div>

                {/* Refund Breakdown */}
                <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '12px', marginTop: '12px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Breakdown
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Down Payment Refund</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formatCurrency(refundCalculation.calculation.downPaymentRefund)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Installments Refund</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formatCurrency(refundCalculation.calculation.installmentsRefund)}</span>
                    </div>
                    {refundCalculation.calculation.totalProcessingFee > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#EF4444' }}>
                        <span>Processing Fee</span>
                        <span style={{ fontWeight: 600 }}>-{formatCurrency(refundCalculation.calculation.totalProcessingFee)}</span>
                      </div>
                    )}
                    <Divider />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '15px' }}>
                      <span style={{ color: 'var(--text-primary)' }}>Net Refund</span>
                      <span style={{ color: '#10B981' }}>{formatCurrency(refundCalculation.calculation.netRefundAmount)}</span>
                    </div>
                  </div>
                </div>

                {/* Refund Method */}
                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Refund Method *
                  </label>
                  <select 
                    value={refundMethod}
                    onChange={(e) => setRefundMethod(e.target.value)}
                    style={{
                      width: '100%',
                      background: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px 12px',
                      color: 'var(--input-text)',
                      fontSize: '13px',
                      outline: 'none',
                    }}
                  >
                    <option value="CASH">Cash</option>
                    <option value="CHECK">Check</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="GCASH">GCash</option>
                  </select>
                </div>

                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Reference Number
                  </label>
                  <input
                    type="text"
                    value={refundReference}
                    onChange={(e) => setRefundReference(e.target.value)}
                    placeholder="Check #, Transaction ID, etc."
                    style={{
                      width: '100%',
                      background: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px 12px',
                      color: 'var(--input-text)',
                      fontSize: '13px',
                      outline: 'none',
                    }}
                  />
                </div>

                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Notes
                  </label>
                  <textarea
                    value={refundNotes}
                    onChange={(e) => setRefundNotes(e.target.value)}
                    placeholder="Optional notes about this refund..."
                    rows={2}
                    style={{
                      width: '100%',
                      background: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px 12px',
                      color: 'var(--input-text)',
                      fontSize: '13px',
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
              </div>
            )}

            {!loadingRefund && refundCalculation?.error && (
              <div style={{ 
                background: 'rgba(239,68,68,0.10)', 
                border: '1px solid rgba(239,68,68,0.30)',
                borderRadius: 'var(--radius-md)', 
                padding: '12px',
                marginBottom: '16px',
                fontSize: '13px',
                color: 'var(--text-secondary)'
              }}>
                <Icon icon="solar:info-circle-bold-duotone" width={16} style={{ marginRight: '8px', verticalAlign: 'middle', color: '#EF4444' }} />
                {refundCalculation.error}
              </div>
            )}

            {!loadingRefund && refundCalculation && !refundCalculation.error && refundCalculation.calculation?.netRefundAmount === 0 && (
              <div style={{ 
                background: 'var(--bg-elevated)', 
                border: '1px solid var(--border-base)',
                borderRadius: 'var(--radius-md)', 
                padding: '12px',
                marginBottom: '16px',
                fontSize: '13px',
                color: 'var(--text-secondary)',
                textAlign: 'center'
              }}>
                <Icon icon="solar:info-circle-bold-duotone" width={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                No refund applicable for this sale
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <Button variant="ghost" onClick={() => { setShowCancelConfirm(false); setCancelingSale(null); }} disabled={processingRefund}>
                Keep Sale
              </Button>
              <Button variant="danger" icon="solar:close-circle-bold-duotone" onClick={confirmCancelSale} disabled={processingRefund}>
                {processingRefund ? 'Processing...' : (refundCalculation?.calculation?.netRefundAmount > 0 ? 'Cancel & Refund' : 'Cancel Sale')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Sales;
